/**
 * Gemini CLI Parser
 *
 * Parses Gemini CLI commands for adding MCP servers.
 *
 * Supported syntax:
 * - gemini mcp add [options] <name> <commandOrUrl> [args...]
 *
 * Options:
 * - -t, --transport: stdio | sse | http (default: stdio)
 * - -s, --scope: user | project (default: project)
 * - -e, --env: KEY=VALUE (repeatable)
 * - -H, --header: "Header: Value" (repeatable)
 * - --timeout: milliseconds
 * - --trust: bypass tool confirmation
 * - --description: server description
 * - --include-tools: comma-separated list of tools to include
 * - --exclude-tools: comma-separated list of tools to exclude
 */

import type { CliParseOptions, CliParseResult, ParsedCliFlags } from '../types';
import { createEmptyParsedFlags } from '../types';
import { BaseCliParser } from './base-parser';

class GeminiCliParser extends BaseCliParser {
  readonly tool = 'gemini' as const;

  readonly displayName = 'Gemini CLI';

  readonly prefixes = ['gemini mcp add'];

  parse({ tokens }: CliParseOptions): CliParseResult {
    const warnings: string[] = [];

    // Skip prefix tokens: gemini, mcp, add
    const args = tokens.slice(3);
    if (args.length === 0) {
      return this.failResult('Missing server name and configuration');
    }

    const flags = GeminiCliParser.parseFlags(args, warnings);
    return this.buildResult(flags, warnings);
  }

  /**
   * Parse flags and positional arguments from the command
   */
  private static parseFlags(args: string[], warnings: string[]): ParsedCliFlags {
    const flags = createEmptyParsedFlags();
    let i = 0;
    const positionalArgs: string[] = [];

    while (i < args.length) {
      const arg = args[i];

      if (arg === '--transport' || arg === '-t') {
        // Transport flag
        i += 1;
        const value = args[i];
        if (value === 'stdio' || value === 'http' || value === 'sse') {
          flags.transport = value;
        } else {
          warnings.push(`Invalid transport type: ${value}. Using default.`);
        }
        i += 1;
      } else if (arg === '--scope' || arg === '-s') {
        // Scope flag (informational only)
        i += 1;
        flags.scope = args[i];
        i += 1;
      } else if (arg === '--env' || arg === '-e') {
        // Environment variable flag
        i += 1;
        const envVar = args[i];
        const parsed = BaseCliParser.parseEnvVar(envVar);
        if (parsed) {
          flags.env[parsed.key] = parsed.value;
        } else {
          warnings.push(`Invalid env format: ${envVar}. Expected KEY=VALUE.`);
        }
        i += 1;
      } else if (arg === '--header' || arg === '-H') {
        // Header flag
        i += 1;
        const header = args[i];
        const parsed = BaseCliParser.parseHeader(header);
        if (parsed) {
          flags.headers[parsed.key] = parsed.value;
        } else {
          warnings.push(`Invalid header format: ${header}. Expected "Name: Value".`);
        }
        i += 1;
      } else if (arg === '--timeout') {
        // Timeout flag
        i += 1;
        const value = args[i];
        const timeout = parseInt(value, 10);
        if (!Number.isNaN(timeout) && timeout > 0) {
          flags.timeout = timeout;
        } else {
          warnings.push(`Invalid timeout value: ${value}. Expected positive integer.`);
        }
        i += 1;
      } else if (arg === '--trust') {
        // Trust flag (boolean, no value)
        // We don't use this but acknowledge it
        i += 1;
      } else if (arg === '--description') {
        // Description flag
        i += 1;
        flags.description = args[i];
        i += 1;
      } else if (arg === '--include-tools') {
        // Include tools flag
        i += 1;
        const value = args[i];
        flags.includeTools = value.split(',').map((t) => t.trim());
        i += 1;
      } else if (arg === '--exclude-tools') {
        // Exclude tools flag
        i += 1;
        const value = args[i];
        flags.excludeTools = value.split(',').map((t) => t.trim());
        i += 1;
      } else if (arg.startsWith('--')) {
        // Unknown flag with potential value
        const nextArg = args[i + 1];
        if (nextArg && !nextArg.startsWith('-')) {
          flags.unknownFlags.push(arg);
          i += 2;
        } else {
          flags.unknownFlags.push(arg);
          i += 1;
        }
      } else if (arg.startsWith('-') && arg.length === 2) {
        // Short flag (unknown)
        const nextArg = args[i + 1];
        if (nextArg && !nextArg.startsWith('-')) {
          flags.unknownFlags.push(arg);
          i += 2;
        } else {
          flags.unknownFlags.push(arg);
          i += 1;
        }
      } else {
        // Positional argument
        positionalArgs.push(arg);
        i += 1;
      }
    }

    // Process positional arguments
    // Format: <name> <commandOrUrl> [args...]
    const [name, secondArg, ...restArgs] = positionalArgs;
    if (name !== undefined) {
      flags.name = name;

      if (secondArg !== undefined) {
        // Check if it's a URL or command
        if (BaseCliParser.isUrl(secondArg)) {
          flags.url = secondArg;
          // Any remaining positional args are unexpected for URL transport
          if (restArgs.length > 0) {
            warnings.push('Extra arguments after URL will be ignored');
          }
        } else {
          // Treat as command
          flags.command = secondArg;
          flags.args = restArgs;
        }
      }
    }

    return flags;
  }
}

// Export singleton instance
export const geminiParser = new GeminiCliParser();
